import { useEffect, useRef, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

// Auth/Login/logout
import { useMsal } from '@azure/msal-react';
import AzureMSALContext, { formatRoleLabel } from 'contexts/AzureMSALContext';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    Card,
    CardContent,
    Chip,
    ClickAwayListener,
    Divider,
    Grid,
    InputAdornment,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    OutlinedInput,
    Paper,
    Popper,
    Stack,
    Switch,
    Typography
} from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import UpgradePlanCard from './UpgradePlanCard';
// import useAuth from 'hooks/useAuth';
import { useIntl } from "react-intl";

// assets
import { IconLogout, IconSearch, IconSettings, IconUser } from '@tabler/icons';
import useConfig from 'hooks/useConfig';

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = () => {
    const { formatMessage: f } = useIntl();
    const { accounts } = useMsal();
    const { logout, userRole, userSite, roles, selectedRole, switchRole } = useContext(AzureMSALContext);
    const theme = useTheme();
    const { borderRadius } = useConfig();
    const navigate = useNavigate();

    const [sdm, setSdm] = useState(true);
    const [value, setValue] = useState('');
    const [notification, setNotification] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    // const { logout, user } = useAuth();
    const [open, setOpen] = useState(false);
    /**
     * anchorRef is used on different components and specifying one type leads to other components throwing an error
     * */
    const anchorRef = useRef(null);
    const handleLogout = () => {
        logout();
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };
    const handleListItemClick = (event, index, route = '') => {
        setSelectedIndex(index);
        handleClose(event);

        if (route && route !== '') {
            navigate(route);
        }
    };
    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }

        prevOpen.current = open;
    }, [open]);

    // Single source of truth for the active-role label so the header chip and
    // this dropdown subtitle stay in sync (formatRoleLabel handles bare "Admin",
    // "Admin.<x>", and "<Site>.<RoleName>" consistently).
    const getRole = () => formatRoleLabel(selectedRole);
    return (
        <>
            <Chip
                sx={{
                    height: '48px',
                    alignItems: 'center',
                    borderRadius: '27px',
                    transition: 'all .2s ease-in-out',
                    borderColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                    '&[aria-controls="menu-list-grow"], &:hover': {
                        borderColor: theme.palette.primary.main,
                        background: `${theme.palette.primary.main}!important`,
                        color: theme.palette.primary.light,
                        '& svg': {
                            stroke: theme.palette.primary.light
                        }
                    },
                    '& .MuiChip-label': {
                        lineHeight: 0
                    }
                }}
                icon={
                    <Avatar
                        src=<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-user" width="44" height="44" viewBox="0 0 24 24" stroke-width="1.5" stroke="#2c3e50" fill="none" stroke-linecap="round" stroke-linejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <circle cx="12" cy="7" r="4" />
                            <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
                        </svg>
                        sx={{
                            ...theme.typography.mediumAvatar,
                            margin: '8px 0 8px 8px !important',
                            cursor: 'pointer'
                        }}
                        ref={anchorRef}
                        aria-controls={open ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                        color="inherit"
                    />
                }
                label={<IconSettings stroke={1.5} size="1.5rem" color={theme.palette.primary.main} />}
                variant="outlined"
                ref={anchorRef}
                aria-controls={open ? 'menu-list-grow' : undefined}
                aria-haspopup="true"
                onClick={handleToggle}
                color="primary"
            />

            <Popper
                placement="bottom"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 14]
                            }
                        }
                    ]
                }}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Transitions in={open} {...TransitionProps}>
                            <Paper>
                                {open && (
                                    <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                        <Box sx={{ p: 2, pb: 0 }}>
                                            <Stack>
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <Typography variant="h4">{f({ id: "hello" })}, </Typography>
                                                    <Typography component="span" variant="h4" sx={{ fontWeight: 400 }}>
                                                        {accounts.length > 0 ? accounts[0].name : ''}
                                                    </Typography>
                                                </Stack>
                                                <Typography variant="subtitle2">{getRole()}</Typography>
                                            </Stack>
                                            <Divider />
                                        </Box>
                                        <PerfectScrollbar style={{ height: '100%', maxHeight: 'calc(100vh - 250px)', overflowX: 'hidden' }}>
                                            <Box sx={{ p: 2, pt: 0 }}>
                                                {roles && roles.length > 1 && (
                                                    <>
                                                        <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                                                            Switch role
                                                        </Typography>
                                                        <List
                                                            component="nav"
                                                            sx={{
                                                                width: '100%',
                                                                maxWidth: 350,
                                                                minWidth: 300,
                                                                backgroundColor: theme.palette.background.paper,
                                                                borderRadius: '10px',
                                                                [theme.breakpoints.down('md')]: {
                                                                    minWidth: '100%'
                                                                },
                                                                '& .MuiListItemButton-root': {
                                                                    mt: 0.5
                                                                }
                                                            }}
                                                        >
                                                            {roles.map((r) => (
                                                                <ListItemButton
                                                                    key={r}
                                                                    sx={{ borderRadius: `${borderRadius}px` }}
                                                                    selected={r === selectedRole}
                                                                    onClick={() => {
                                                                        switchRole(r);
                                                                        setOpen(false);
                                                                        // Route table swaps between MainRoutes /
                                                                        // MainRoutesAdmin based on isGlobalAdmin. If
                                                                        // the user is on a path that exists in only
                                                                        // one of those, the post-switch render would
                                                                        // land on a blank route. Sending them to '/'
                                                                        // keeps them on a known-valid page.
                                                                        navigate('/');
                                                                    }}
                                                                >
                                                                    <ListItemText
                                                                        primary={
                                                                            <Typography variant="body2">
                                                                                {formatRoleLabel(r)}
                                                                                {r === selectedRole && (
                                                                                    <Typography
                                                                                        component="span"
                                                                                        variant="caption"
                                                                                        sx={{ ml: 1, color: 'primary.main' }}
                                                                                    >
                                                                                        (active)
                                                                                    </Typography>
                                                                                )}
                                                                            </Typography>
                                                                        }
                                                                    />
                                                                </ListItemButton>
                                                            ))}
                                                        </List>
                                                        <Divider sx={{ my: 1 }} />
                                                    </>
                                                )}
                                                <List
                                                    component="nav"
                                                    sx={{
                                                        width: '100%',
                                                        maxWidth: 350,
                                                        minWidth: 300,
                                                        backgroundColor: theme.palette.background.paper,
                                                        borderRadius: '10px',
                                                        [theme.breakpoints.down('md')]: {
                                                            minWidth: '100%'
                                                        },
                                                        '& .MuiListItemButton-root': {
                                                            mt: 0.5
                                                        }
                                                    }}
                                                >
                                                    <ListItemButton
                                                        sx={{ borderRadius: `${borderRadius}px` }}
                                                        selected={selectedIndex === 4}
                                                        onClick={handleLogout}
                                                    >
                                                        <ListItemIcon>
                                                            <IconLogout stroke={1.5} size="1.3rem" />
                                                        </ListItemIcon>
                                                        <ListItemText primary={<Typography variant="body2">Logout</Typography>} />
                                                    </ListItemButton>
                                                </List>
                                            </Box>
                                        </PerfectScrollbar>
                                    </MainCard>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};

export default ProfileSection;
